import { parseInline, toLines } from "@/lib/richText";

/**
 * Renders the CMS `richBlocks` shape — the array of {type, text} blocks the
 * admin block editor produces.
 *
 * This started life inside BlogDetail. The legal pages need exactly the same
 * rendering, and a second copy would have drifted the moment either page
 * gained a block type.
 */
export default function RichContent({ blocks, className = "" }) {
  if (!Array.isArray(blocks)) return null;

  return (
    <div className={`prose-athena ${className}`}>
      {blocks.map((b, i) => {
        switch (b.type) {
          case "h2":
            return (
              <h2 key={i} className="text-2xl md:text-3xl font-bold text-ink mt-10 mb-4 tracking-tight">
                {parseInline(b.text)}
              </h2>
            );

          case "h3":
            return (
              <h3 key={i} className="text-xl md:text-2xl font-bold text-ink mt-8 mb-3 tracking-tight">
                {parseInline(b.text)}
              </h3>
            );

          case "quote":
            return (
              <blockquote key={i} className="my-8 border-l-4 border-aqua pl-6 text-xl md:text-2xl font-semibold text-ink italic">
                {parseInline(b.text)}
              </blockquote>
            );

          case "ul":
            return (
              <ul key={i} className="mb-5 space-y-2 list-disc pl-6 text-greyish text-lg leading-relaxed">
                {toLines(b.text).map((line, j) => (
                  <li key={j}>{parseInline(line)}</li>
                ))}
              </ul>
            );

          case "ol":
            return (
              <ol key={i} className="mb-5 space-y-2 list-decimal pl-6 text-greyish text-lg leading-relaxed">
                {toLines(b.text).map((line, j) => (
                  <li key={j}>{parseInline(line)}</li>
                ))}
              </ol>
            );

          case "image":
            return b.url ? (
              <figure key={i} className="my-8">
                <img src={b.url} alt={b.alt || ""} loading="lazy" decoding="async" className="w-full rounded-lg" />
                {b.caption && (
                  <figcaption className="mt-2 text-sm text-greyish text-center">{b.caption}</figcaption>
                )}
              </figure>
            ) : null;

          default:
            return (
              <p key={i} className="text-greyish leading-relaxed text-lg mb-5 whitespace-pre-line">
                {parseInline(b.text)}
              </p>
            );
        }
      })}
    </div>
  );
}
